import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import Honeybadger from '@honeybadger-io/js';
import CashApi from '../services/CashApi';
import stripeAccountLoadStates from '../constants/stripeAccount';
import noop from '../utils/noop';

const defaults = {
  stripeAccount: null,
  fetchStripeAccount: noop,
  setStripeAccount: noop,
  isFetching: false,
  accountLoadState: stripeAccountLoadStates.READY,
  error: null,
};

const Context = createContext(defaults);

export default Context;

const normalizeStripeAccount = payload => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  return {
    memberId: payload.memberId || payload.member_id || null,
    stripeId: payload.stripeId || payload.stripe_id || null,
    // Legacy V1 accounts evaluate to null here because account_version was introduced in V2
    accountVersion: payload.accountVersion || payload.account_version || null,
    recipientStripeTransfersStatus:
      payload.recipientStripeTransfersStatus ||
      payload.recipient_stripe_transfers_status ||
      null,
    recipientPayoutsStatus:
      payload.recipientPayoutsStatus ||
      payload.recipient_payouts_status ||
      null,
    storerHoldsUsdStatus:
      payload.storerHoldsUsdStatus || payload.storer_holds_usd_status || null,
    storerOutboundBankTransfersStatus:
      payload.storerOutboundBankTransfersStatus ||
      payload.storer_outbound_bank_transfers_status ||
      null,
    capabilityStatus:
      payload.capabilityStatus ||
      payload.treasuryCapabilityStatus ||
      payload.treasury_capability_status ||
      null,
    disabledReason: payload.disabledReason || payload.disabled_reason || null,
  };
};

export const StripeAccountContextProvider = ({children}) => {
  // `undefined` = initial fetch pending. `null` = complete & no account.
  const [stripeAccount, setStripeAccountInternal] = useState(undefined);
  const [isFetching, setIsFetching] = useState(true);
  const [errorState, setErrorState] = useState(null);

  /**
   * Refetches account details from CashApi.
   * Empty dependency array [] ensures a stable memory reference, preventing
   * infinite refetch loops in components that list fetchStripeAccount in useEffect.
   */
  const fetchStripeAccount = useCallback(async () => {
    setIsFetching(true);

    try {
      const response = await CashApi.getStripeAccountDetails();

      if (response.ok) {
        const payload = await response.json();
        const normalized = normalizeStripeAccount(payload);

        setStripeAccountInternal(normalized);
        setErrorState(null);
        setIsFetching(false);

        return normalized;
      }

      if (response.status === 404) {
        setStripeAccountInternal(null);
        setErrorState(null);
        setIsFetching(false);

        return null;
      }

      throw Error(response.statusText);
    } catch (error) {
      Honeybadger.notify(error, 'FetchStripeAccountDetailsError');
      setErrorState(error);
      setStripeAccountInternal(null);
      setIsFetching(false);

      return null;
    }
  }, []);

  /**
   * Preserved for backwards compatibility with components/tests calling setStripeAccount.
   * Normalizes whatever is passed to guarantee consistent state shape and prevent undefined.
   */
  const setStripeAccount = useCallback(rawPayload => {
    const normalized = normalizeStripeAccount(rawPayload);

    setErrorState(null);
    setIsFetching(false);
    setStripeAccountInternal(normalized);
  }, []);

  useEffect(() => {
    if (stripeAccount === undefined) {
      fetchStripeAccount();
    }
  }, [fetchStripeAccount, stripeAccount]);

  const contextValue = useMemo(
    () => ({
      stripeAccount: stripeAccount ?? null,
      fetchStripeAccount,
      setStripeAccount,
      isFetching,
      accountLoadState:
        stripeAccount === undefined
          ? stripeAccountLoadStates.LOADING
          : stripeAccountLoadStates.READY,
      error: errorState,
    }),
    [
      stripeAccount,
      fetchStripeAccount,
      setStripeAccount,
      isFetching,
      errorState,
    ],
  );

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

StripeAccountContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
